/**
 * Replies that mean the server isn't there right now rather than that the
 * request is wrong: the proxy's 502 or 504 while a deploy restarts the API,
 * a 503 while it can't take requests, and no answer at all (the server, or
 * the network, unreachable). Asking again a little later can succeed.
 */
const SERVER_UNAVAILABLE_STATUSES = new Set([502, 503, 504])

/**
 * The code the proxy's 503 carries while a deploy holds the app (from just
 * before database migrations until the new release is healthy). Unlike a
 * server that isn't there, it's a planned wait of a few minutes that ends
 * with a new build: the app shows its updating screen and reloads when it's
 * over (AppUpdateController), so a request isn't retried.
 */
export const UPDATING_CODE = 'updating'
const UPDATING_STATUS = 503

/** Whether a reply is the proxy's "updating" 503 (see `UPDATING_CODE`). */
export function isUpdatingResponse(status: number, body: unknown): boolean {
  if (status !== UPDATING_STATUS) return false
  let data = body
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return false
    }
  }
  return (data as { code?: unknown } | null)?.code === UPDATING_CODE
}

/** Whether a failed request was turned away because the app is updating. */
export function isUpdatingError(error: unknown): boolean {
  const { status, code } = (error ?? {}) as { status?: unknown; code?: unknown }
  return status === UPDATING_STATUS && code === UPDATING_CODE
}

/**
 * Tries after the first while the server is unavailable. With the delays
 * below they wait about 20 seconds, more than a deploy's restart takes
 * (the proxy also holds each request up to 20s; see the root Caddyfile).
 */
export const SERVER_UNAVAILABLE_RETRIES = 6
const SERVER_UNAVAILABLE_MAX_DELAY_MS = 5_000

/**
 * Whether a failed request means the server is unavailable for now (see
 * above). The updating 503 is its own case (`isUpdatingError`).
 */
export function isServerUnavailableError(error: unknown): boolean {
  if (isUpdatingError(error)) return false
  const { status, code, timedOut } = (error ?? {}) as {
    status?: unknown
    code?: unknown
    timedOut?: unknown
  }
  if (typeof status === 'number') return SERVER_UNAVAILABLE_STATUSES.has(status)
  return code === 'network_error' && timedOut !== true
}

/**
 * The app's default query retry. While the server is unavailable (a deploy
 * restarting it), keep trying for a while, so a page waits instead of
 * failing. Otherwise one more try after a network failure or a server error,
 * none after a client error. A 404 or 403 won't change when asked again, and
 * retrying only holds the Not Found or No Access screen back by a second. A
 * timeout (408) or a rate limit (429) can pass later, so they keep their
 * retry. While the app is updating, nothing is retried: the updating screen
 * covers the page and reloads it once the update is over.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isUpdatingError(error)) return false
  if (isServerUnavailableError(error)) return failureCount < SERVER_UNAVAILABLE_RETRIES
  if (failureCount >= 1) return false
  const status = (error as { status?: unknown } | null)?.status
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return status === 408 || status === 429
  }
  return true
}

/**
 * How long to wait before the next try, given the failures before the last
 * one (TanStack Query passes 0 after the first): 1s, 2s, 4s, then 5s each.
 */
export function queryRetryDelay(failureCount: number): number {
  return Math.min(1000 * 2 ** failureCount, SERVER_UNAVAILABLE_MAX_DELAY_MS)
}
